import { useMixpanel } from '../integrations/useMixpanel.hook';
import { useBraze } from '../integrations/useBraze.hook';
import { useSelector } from 'react-redux';
import { selectAnalyticsSuperAttributes } from '@/store/selectors';
import { EPageType, PageViewEventPayload } from '@/models/analytics.model';
import {
  ANALYTICS,
  PAGE_VIEW_SOURCE,
  PAGE_VIEW_TYPE_STORAGE_KEY,
  PREV_PAGE_STORAGE_KEY,
  SOURCE,
  UTM_SOURCE,
} from '@/constants/analytics/analytics';
import { useSearchParams } from 'react-router-dom';
import { useGtmEcommerce, GtmEcommercePayload } from './useGtmEcommerce.hook';
import { GtmEcommerceEventName } from '@/utils/analytics/gtm-ecommerce';
import { getCurrentScreenName, logger } from '@/utils';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';

export enum EAnalyticsProvider {
  braze = 'braze',
  mixpanel = 'mixpanel',
  gtm = 'gtm',
  All = 'all',
}

export const BRAZE_ONLY: EAnalyticsProvider[] = [EAnalyticsProvider.braze];

export const useAnalytics = () => {
  const mpHook = useMixpanel();
  const brazeHook = useBraze();
  const gtmHook = useGtmEcommerce();
  const superAttributes = useSelector(selectAnalyticsSuperAttributes);
  const [searchParams] = useSearchParams();

  const getPrevUrlPath = (eventPayload: PageViewEventPayload) => {
    const prevUrlPath =
      sessionStorage.getItem(PREV_PAGE_STORAGE_KEY) || 'Other';
    sessionStorage.setItem(PREV_PAGE_STORAGE_KEY, window.location.pathname);
    sessionStorage.setItem(
      PAGE_VIEW_TYPE_STORAGE_KEY,
      eventPayload.pageType || 'Other'
    );
    return prevUrlPath;
  };

  const getPageSource = (eventPayload?: PageViewEventPayload): string => {
    const pageSource = searchParams.get(PAGE_VIEW_SOURCE);
    const utmSource = searchParams.get(UTM_SOURCE)?.split('_')?.join(' ');
    const qsSource = searchParams.get(SOURCE);
    if (pageSource) {
      const newParams = new URLSearchParams(searchParams); // Clone the current search params
      newParams.delete(PAGE_VIEW_SOURCE);
      const url = new URL(window.location.href);
      url.search = newParams.toString();
      // remove query param without rerender the component
      window.history.replaceState({}, '', url.toString());
    }
    return (
      pageSource || utmSource || qsSource || eventPayload?.source || 'Other'
    );
  };

  const firePageViewEvent = (eventPayload: PageViewEventPayload) => {
    const screen = eventPayload.screen || eventPayload.eventName;
    const payload = {
      [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.PAGE_VIEW,
      [ANALYTICS.PROPERTIES.SCREEN]: screen,
      [ANALYTICS.PROPERTIES.REFERRER_URL_PATH]: getPrevUrlPath(eventPayload),
      [ANALYTICS.PROPERTIES.PAGE_TYPE]: eventPayload.pageType,
      [ANALYTICS.PROPERTIES.SOURCE]: getPageSource(eventPayload),
      ...eventPayload.extraProperties,
    };
    fireAnalyticsEvent(eventPayload.eventName, payload);
  };

  const firePremiumHookClickEvent = (
    source: string,
    isPremiumHook: boolean,
    pageType = EPageType.product,
    analyticsProvider?: EAnalyticsProvider[]
  ) => {
    fireAnalyticsEvent(
      ANALYTICS.EVENTS.PREMIUM_HOOK_CLICK,
      {
        [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.CLICK,
        [ANALYTICS.PROPERTIES.PAGE_TYPE]: pageType,
        [ANALYTICS.PROPERTIES.SOURCE]: source,
        [ANALYTICS.PROPERTIES.SCREEN]: getCurrentScreenName(),
        [ANALYTICS.PROPERTIES.IS_PREMIUM_HOOK]: isPremiumHook,
      },
      analyticsProvider
    );
  };

  /**
   * Sends an analytics event to one or more providers.
   *
   * @param eventName - The name of the event to track.
   * @param payload - Event data. For GTM ecommerce, must include { plan } or { plan, paymentType }.
   * @param providers - Target providers (defaults to Mixpanel only).
   *
   *  Usage examples:
   *   - Default (Mixpanel only):
   *       fireAnalyticsEvent('login', payload);
   *   - Send to Braze only:
   *       fireAnalyticsEvent('login', payload, [EAnalyticsProvider.braze]);
   *   - Send to all:
   *       fireAnalyticsEvent('login', payload, [EAnalyticsProvider.All]);
   */
  const fireAnalyticsEvent = (
    eventName: string,
    payload: Record<string, any> = {},
    providers: EAnalyticsProvider[] = [EAnalyticsProvider.mixpanel]
  ): void => {
    try {
      const shouldSendTo = (provider: EAnalyticsProvider): boolean =>
        providers.includes(EAnalyticsProvider.All) ||
        providers.includes(provider);

      if (shouldSendTo(EAnalyticsProvider.mixpanel)) {
        mpHook.track(eventName, payload);
      }

      if (shouldSendTo(EAnalyticsProvider.braze)) {
        brazeHook.track(eventName, { ...superAttributes, ...payload });
      }

      if (shouldSendTo(EAnalyticsProvider.gtm)) {
        gtmHook.fireGtmEcommerceEvent(
          eventName as GtmEcommerceEventName,
          payload as GtmEcommercePayload
        );
      }
    } catch (e) {
      logger.error('Analytics event failed', e, {
        tags: {
          feature: LOGGER_FEATURE.ANALYTICS,
          action: LOGGER_ACTION.TRACK,
        },
        extra: { eventName },
      });
    }
  };

  return {
    fireAnalyticsEvent,
    firePageViewEvent,
    firePremiumHookClickEvent,
    getPrevUrlPath,
    getPageSource,
  };
};
